import { AxialCoord } from '../types/terrain';

/**
 * Player colors for multiplayer marker segmentation
 * Each player will be assigned a distinct color
 */
export const PLAYER_COLORS = [
  '#4A90E2', // Blue
  '#E24A4A', // Red
  '#4AE290', // Green
  '#E2C44A', // Gold
];

/**
 * Represents a player on a hex for rendering purposes
 */
export interface PlayerMarker {
  playerId: string;
  color: string;
}

/**
 * Draw a segmented player marker on a hex using canvas clipping paths
 * Supports 1-4 players on the same hex
 * 
 * @param ctx - Canvas rendering context
 * @param corners - Array of 6 corner points defining the hexagon
 * @param players - Array of player markers (1-4 players)
 * @param playerImage - The player marker image to use
 */
export function drawSegmentedPlayerMarker(
  ctx: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  players: PlayerMarker[],
  playerImage: HTMLImageElement
): void {
  if (players.length === 0 || corners.length !== 6) return;

  // Calculate hex bounding box
  const minX = Math.min(...corners.map(c => c.x));
  const maxX = Math.max(...corners.map(c => c.x));
  const minY = Math.min(...corners.map(c => c.y));
  const maxY = Math.max(...corners.map(c => c.y));
  
  const centerX = (minX + maxX) / 2;
  const centerY = (minY + maxY) / 2;
  const hexWidth = maxX - minX;
  const hexHeight = maxY - minY;

  ctx.save();
  ctx.globalAlpha = 1;
  ctx.globalCompositeOperation = 'source-over';

  if (players.length === 1) {
    // Single player - draw full marker without color tint (normal appearance)
    drawHexClip(ctx, corners);
    ctx.clip();
    
    // Draw the image normally (no color tint needed for single player)
    ctx.drawImage(playerImage, minX, minY, hexWidth, hexHeight);
    
    ctx.restore();
  } else if (players.length === 2) {
    // Two players - vertical split
    drawTwoPlayerSegments(ctx, corners, centerX, centerY, players, playerImage, minX, minY, hexWidth, hexHeight);
  } else if (players.length === 3) {
    // Three players - 120° pie slices
    drawThreePlayerSegments(ctx, corners, centerX, centerY, players, playerImage, minX, minY, hexWidth, hexHeight);
  } else if (players.length === 4) {
    // Four players - 90° quadrants
    drawFourPlayerSegments(ctx, corners, centerX, centerY, players, playerImage, minX, minY, hexWidth, hexHeight);
  }

  ctx.restore();
}

/**
 * Create a hexagonal clipping path
 */
function drawHexClip(ctx: CanvasRenderingContext2D, corners: { x: number; y: number }[]): void {
  ctx.beginPath();
  ctx.moveTo(corners[0].x, corners[0].y);
  for (let i = 1; i < 6; i++) {
    ctx.lineTo(corners[i].x, corners[i].y);
  }
  ctx.closePath();
}

/**
 * Draw marker for 2 players (vertical split)
 */
function drawTwoPlayerSegments(
  ctx: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  centerX: number,
  centerY: number,
  players: PlayerMarker[],
  playerImage: HTMLImageElement,
  minX: number,
  minY: number,
  hexWidth: number,
  hexHeight: number
): void {
  // Left half - Player 1
  ctx.save();
  ctx.beginPath();
  // Create left half clip: hex clip AND left side of center line
  drawHexClip(ctx, corners);
  ctx.clip();
  
  // Add vertical center line clip (left side)
  ctx.beginPath();
  ctx.rect(minX, minY, (centerX - minX), hexHeight);
  ctx.clip();
  
  // Draw image and tint
  ctx.drawImage(playerImage, minX, minY, hexWidth, hexHeight);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = players[0].color;
  ctx.fillRect(minX, minY, hexWidth, hexHeight);
  ctx.restore();

  // Right half - Player 2
  ctx.save();
  ctx.beginPath();
  drawHexClip(ctx, corners);
  ctx.clip();
  
  // Add vertical center line clip (right side)
  const maxX = minX + hexWidth;
  ctx.beginPath();
  ctx.rect(centerX, minY, (maxX - centerX), hexHeight);
  ctx.clip();
  
  // Draw image and tint
  ctx.drawImage(playerImage, minX, minY, hexWidth, hexHeight);
  ctx.globalCompositeOperation = 'multiply';
  ctx.fillStyle = players[1].color;
  ctx.fillRect(minX, minY, hexWidth, hexHeight);
  ctx.restore();

  // Draw divider line (clipped to hex boundary)
  ctx.save();
  
  // Clip to hex shape so line doesn't extend beyond
  drawHexClip(ctx, corners);
  ctx.clip();
  
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  const maxY = minY + hexHeight;
  ctx.beginPath();
  ctx.moveTo(centerX, minY);
  ctx.lineTo(centerX, maxY);
  ctx.stroke();
  ctx.restore();
}

/**
 * Draw marker for 3 players (120° segments)
 */
function drawThreePlayerSegments(
  ctx: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  centerX: number,
  centerY: number,
  players: PlayerMarker[],
  playerImage: HTMLImageElement,
  minX: number,
  minY: number,
  hexWidth: number,
  hexHeight: number
): void {
  const maxX = minX + hexWidth;
  const maxY = minY + hexHeight;
  
  // Three 120° segments starting from top
  for (let i = 0; i < 3; i++) {
    ctx.save();
    
    // Hex clip first
    drawHexClip(ctx, corners);
    ctx.clip();
    
    // Create 120° wedge clip
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    
    // Start angle for this segment (starting from -90° / top)
    const startAngle = -Math.PI / 2 + (i * 2 * Math.PI / 3);
    const endAngle = startAngle + (2 * Math.PI / 3);
    
    ctx.arc(centerX, centerY, hexWidth, startAngle, endAngle);
    ctx.closePath();
    ctx.clip();
    
    // Draw image and tint
    ctx.drawImage(playerImage, minX, minY, hexWidth, hexHeight);
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = players[i].color;
    ctx.fillRect(minX, minY, hexWidth, hexHeight);
    
    ctx.restore();
  }

  // Draw divider lines from center (clipped to hex boundary)
  ctx.save();
  
  // Clip to hex shape so lines don't extend beyond
  drawHexClip(ctx, corners);
  ctx.clip();
  
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  for (let i = 0; i < 3; i++) {
    const angle = -Math.PI / 2 + (i * 2 * Math.PI / 3);
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.lineTo(
      centerX + Math.cos(angle) * hexWidth,
      centerY + Math.sin(angle) * hexWidth
    );
    ctx.stroke();
  }
  ctx.restore();
}

/**
 * Draw marker for 4 players (90° quadrants)
 */
function drawFourPlayerSegments(
  ctx: CanvasRenderingContext2D,
  corners: { x: number; y: number }[],
  centerX: number,
  centerY: number,
  players: PlayerMarker[],
  playerImage: HTMLImageElement,
  minX: number,
  minY: number,
  hexWidth: number,
  hexHeight: number
): void {
  const maxX = minX + hexWidth;
  const maxY = minY + hexHeight;
  
  // Four 90° quadrants
  const quadrants = [
    { startAngle: -Math.PI / 2, endAngle: 0 },           // Top-right
    { startAngle: 0, endAngle: Math.PI / 2 },            // Bottom-right
    { startAngle: Math.PI / 2, endAngle: Math.PI },      // Bottom-left
    { startAngle: Math.PI, endAngle: 3 * Math.PI / 2 },  // Top-left
  ];
  
  for (let i = 0; i < 4; i++) {
    ctx.save();
    
    // Hex clip first
    drawHexClip(ctx, corners);
    ctx.clip();
    
    // Create 90° wedge clip
    const { startAngle, endAngle } = quadrants[i];
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, hexWidth, startAngle, endAngle);
    ctx.closePath();
    ctx.clip();
    
    // Draw image and tint
    ctx.drawImage(playerImage, minX, minY, hexWidth, hexHeight);
    ctx.globalCompositeOperation = 'multiply';
    ctx.fillStyle = players[i].color;
    ctx.fillRect(minX, minY, hexWidth, hexHeight);
    
    ctx.restore();
  }

  // Draw divider lines (cross pattern, clipped to hex boundary)
  ctx.save();
  
  // Clip to hex shape so lines don't extend beyond
  drawHexClip(ctx, corners);
  ctx.clip();
  
  ctx.strokeStyle = '#fff';
  ctx.lineWidth = 2;
  
  // Vertical line
  ctx.beginPath();
  ctx.moveTo(centerX, minY);
  ctx.lineTo(centerX, maxY);
  ctx.stroke();
  
  // Horizontal line
  ctx.beginPath();
  ctx.moveTo(minX, centerY);
  ctx.lineTo(maxX, centerY);
  ctx.stroke();
  
  ctx.restore();
}

